'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import Header from './Header';
import type { HeaderFields, CFTextImageBlock } from '@/types/contentful';

interface HeaderResolverProps {
  defaultHeader: HeaderFields | null;
  /** slug → HeaderFields for every page with a customHeader in Contentful. Built once in RootLayout. */
  customHeaderMap: Record<string, HeaderFields>;
  appModal?: CFTextImageBlock;
  pathPrefixToStrip?: string;
}

/**
 * Resolves the correct header per route on the client via `usePathname()`.
 *
 * Server-side resolution (layout/template) doesn't work for `<Link>` clicks:
 * Next.js reuses the cached RSC payload for the root segment across sibling
 * [...slug] navigations, so a partner header resolved for `/ida` would leak
 * onto `/kontakt/om-eesy`. `usePathname()` re-renders on every navigation,
 * fixing the leak without any extra server round-trip.
 *
 * Walk-up: `/elforbundet/5g-internet` inherits `/elforbundet`'s header if it
 * doesn't define its own.
 */
export default function HeaderResolver({
  defaultHeader,
  customHeaderMap,
  appModal,
  pathPrefixToStrip,
}: HeaderResolverProps) {
  const pathname = usePathname() ?? '/';
  const contentPath = pathPrefixToStrip && pathname.startsWith(pathPrefixToStrip)
    ? pathname.slice(pathPrefixToStrip.length) || '/'
    : pathname;

  const activeHeader = useMemo<HeaderFields | null>(() => {
    const segments = contentPath.split('/').filter(Boolean);
    for (let i = segments.length; i > 0; i--) {
      const candidate = '/' + segments.slice(0, i).join('/');
      const found = customHeaderMap[candidate];
      if (found) return found;
    }
    return defaultHeader;
  }, [contentPath, customHeaderMap, defaultHeader]);

  if (!activeHeader) return null;

  return <Header fields={activeHeader} appModal={appModal} />;
}
